import useBusinessCategories from '../../useBusinessCategories';
import React, { useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import app from '../../config/firebase';
import { userName } from './adminUtils';
import '../BusinessProfileCreation.css';


export default function AdminAddBusiness({ users, onSaved, onCancel }) {
  const categoryOptions = useBusinessCategories();
  const [query, setQuery] = useState('');
  const [owner, setOwner] = useState(null);
  const [details, setDetails] = useState({ businessName: '', category: '', phone: '', email: '', description: '', address: '', website: '', businessHours: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(null);
  const savedId = useRef(null);
  const busy = useRef(false);
  const matches = users.filter(u => !u.isSuspended && `${userName(u)} ${u.email} ${u.phone} ${u.id}`.toLowerCase().includes(query.trim().toLowerCase()));
  const field = key => ({ value: details[key], onChange: e => setDetails(prev => ({ ...prev, [key]: e.target.value })), maxLength: key === 'description' ? 5000 : 500 });

  const save = async e => {
    e.preventDefault();
    if (busy.current) return;
    if (!details.phone.trim() && !details.email.trim()) { setError('Enter a business phone number or email address.'); return; }
    busy.current = true;
    setSaving(true);
    setError('');
    try {
      if (!requestId.current) requestId.current = window.crypto.randomUUID();
      if (!savedId.current) {
        const create = httpsCallable(getFunctions(app), 'adminCreateBusiness');
        const result = await create({ ...details, ownerId: owner.id, requestId: requestId.current });
        savedId.current = result.data.businessId;
      }
      const snap = await getDoc(doc(getFirestore(app), 'businesses', savedId.current));
      if (!snap.exists()) throw new Error('Business saved, but could not be loaded. Retry to refresh it.');
      onSaved({ ...snap.data(), id: snap.id });
    } catch (err) {
      setError(err.message || 'Could not save the business. Please retry.');
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  return (
    <section className="admin-panel step-panel" aria-labelledby="add-business-title">
      <h2 id="add-business-title">Add Business — {owner ? 'Business details' : 'Select user'}</h2>
      {error && <p className="form-error" role="alert">{error}</p>}
      {!owner ? <>
        <label htmlFor="owner-search">Search name, email, phone or user ID</label>
        <input id="owner-search" type="search" value={query} onChange={e => setQuery(e.target.value)} />
        <p>Select the registered client who will own and manage this business.</p>
        {users.length === 0 && <p>No users loaded. Cancel and refresh the console to try again.</p>}
        {users.length > 0 && matches.length === 0 && <p>No active users match your search.</p>}
        <ul aria-label="Matching users">
          {matches.slice(0, 30).map(u => <li key={u.id}>
            <button type="button" className="icon-text-btn" onClick={() => setOwner(u)}>
              {userName(u)} — {u.email || u.phone || u.id}
            </button>
          </li>)}
        </ul>
        {matches.length > 30 && <p>Showing the first 30 matches. Refine your search to find the client.</p>}
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </> : <form onSubmit={save}>
        <p>Owner: <strong>{userName(owner)}</strong> ({owner.email || owner.id})</p>
        <fieldset disabled={saving || Boolean(savedId.current)} style={{ border: 0, padding: 0, minWidth: 0 }}>
          <button type="button" className="icon-text-btn" onClick={() => { setOwner(null); setError(''); }}>Change user</button>
          <label htmlFor="new-business-name">Business name *</label>
          <input id="new-business-name" required {...field('businessName')} />
          {categoryOptions.error && <p role="alert">{categoryOptions.error} <button type="button" onClick={categoryOptions.refresh}>Retry categories</button></p>}
          <label htmlFor="new-business-category">Category *</label>
          <select id="new-business-category" required value={details.category} onChange={e => setDetails(p => ({ ...p, category: e.target.value }))}>
            <option value="">Select category</option>
            {categoryOptions.categories.map(c => <option key={c}>{c}</option>)}
          </select>
          <p>Provide at least one business contact method.</p>
          <label htmlFor="new-business-phone">Phone</label><input id="new-business-phone" type="tel" {...field('phone')} />
          <label htmlFor="new-business-email">Email</label><input id="new-business-email" type="email" {...field('email')} />
          <label htmlFor="new-business-description">Description (optional)</label><textarea id="new-business-description" rows={3} {...field('description')} />
          <label htmlFor="new-business-address">Address (optional)</label><input id="new-business-address" {...field('address')} />
          <p>An address helps customers find this business in nearby searches.</p>
          <label htmlFor="new-business-website">Website (optional)</label><input id="new-business-website" type="url" placeholder="https://" {...field('website')} />
          <label htmlFor="new-business-hours">Business hours (optional)</label><input id="new-business-hours" placeholder="Mon: 09:00 - 17:00" {...field('businessHours')} />
        </fieldset>
        <p>The business will be saved as active and unverified. Images, services and documents can be added through Edit profile after saving. Banking is optional for creation; payment setup is required to receive payments.</p>
        <div className="step-actions">
          <button type="button" className="btn-secondary" disabled={saving} onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : savedId.current ? 'Refresh saved business' : 'Save Business'}</button>
        </div>
      </form>}
    </section>
  );
}
